/**
 * ChangeOrdersTab
 * ---------------
 * AIA G701-style change orders on a project. Approved change orders roll
 * straight into the project's contract value and substantial-completion date.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Trash2, Download, Check, X, Send, FileWarning, ArrowUpRight, Ban, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const fmt$ = (n) =>
  `${Number(n || 0) < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const CO_TYPES = [
  { value: "owner_request", label: "Owner-requested scope addition" },
  { value: "unforeseen_condition", label: "Unforeseen / concealed condition" },
  { value: "design_change", label: "Design change or drawing revision" },
  { value: "code_compliance", label: "Code / AHJ compliance requirement" },
  { value: "infection_control", label: "Infection control (ICRA) requirement" },
  { value: "schedule_acceleration", label: "Schedule acceleration" },
  { value: "credit", label: "Credit / deduct to owner" },
  { value: "other", label: "Other" },
];

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  rejected: "bg-red-50 text-red-700 ring-red-300",
  void: "bg-slate-100 text-slate-400 ring-slate-200",
};

const emptyItem = () => ({ description: "", qty: 1, uom: "LS", unit_price: 0, unit_cost: 0 });

const blankForm = () => ({
  title: "", description: "", co_type: "owner_request", justification: "",
  division_code: "", responsible_company: "", markup_pct: 0, schedule_impact_days: 0,
  line_items: [emptyItem()],
});

const Stat = ({ label, value, sub, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

export default function ChangeOrdersTab({ projectId, project, token, onContractChange }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [approving, setApproving] = useState(null);
  const [ownerSigner, setOwnerSigner] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/change-orders?project_id=${projectId}`, { headers });
      setRows(r.data?.change_orders || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load change orders");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const subtotal = (form?.line_items || []).reduce(
    (s, li) => s + Number(li.qty || 0) * Number(li.unit_price || 0), 0);
  const markup = subtotal * (Number(form?.markup_pct || 0) / 100);
  const costTotal = (form?.line_items || []).reduce(
    (s, li) => s + Number(li.qty || 0) * Number(li.unit_cost || 0), 0);

  const setItem = (i, key, v) => setForm((f) => ({
    ...f, line_items: f.line_items.map((li, j) => (j === i ? { ...li, [key]: v } : li)),
  }));

  const openNew = () => { setEditingId(null); setForm(blankForm()); };
  const openEdit = (co) => {
    setEditingId(co.id);
    setForm({
      title: co.title || "", description: co.description || "", co_type: co.co_type,
      justification: co.justification || "", division_code: co.division_code || "",
      responsible_company: co.responsible_company || "", markup_pct: co.markup_pct || 0,
      schedule_impact_days: co.schedule_impact_days || 0,
      line_items: (co.line_items || []).length ? co.line_items.map((li) => ({ ...li })) : [emptyItem()],
    });
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the change order a title");
    setSaving(true);
    try {
      const body = {
        ...form,
        markup_pct: Number(form.markup_pct || 0),
        schedule_impact_days: Number(form.schedule_impact_days || 0),
        line_items: form.line_items
          .filter((li) => li.description || li.unit_price)
          .map((li) => ({
            ...li, qty: Number(li.qty || 0), unit_price: Number(li.unit_price || 0),
            unit_cost: Number(li.unit_cost || 0),
            extended: Number(li.qty || 0) * Number(li.unit_price || 0),
          })),
      };
      const r = editingId
        ? await axios.put(`${API}/change-orders/${editingId}`, body, { headers })
        : await axios.post(`${API}/change-orders`, { project_id: projectId, ...body }, { headers });
      toast.success(`${r.data.change_order.number} saved`, { description: fmt$(r.data.change_order.total) });
      setForm(null); setEditingId(null);
      setSummary(r.data.summary); load(); onContractChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the change order");
    } finally { setSaving(false); }
  };

  const act = async (co, action, extra = {}) => {
    try {
      const r = await axios.post(`${API}/change-orders/${co.id}/action`,
        { action, ...extra }, { headers });
      const s = r.data.summary;
      toast.success(`${co.number} ${r.data.change_order.status}`, {
        description: action === "approve"
          ? `Contract value is now ${fmt$(s.revised_contract_value)}`
          : undefined,
      });
      setSummary(s); load(); onContractChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} the change order`);
    }
  };

  const remove = async (co) => {
    if (!window.confirm(`Delete ${co.number}? This cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${API}/change-orders/${co.id}`, { headers });
      setSummary(r.data.summary); load(); onContractChange?.();
      toast.success(`${co.number} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openPdf = async (co) => {
    try {
      const r = await axios.get(`${API}/change-orders/${co.id}/pdf`,
        { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${co.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the PDF");
    }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading change orders…</p>;
  }

  return (
    <div className="space-y-5" data-testid="change-orders-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Change Orders</h2>
          <p className="text-sm text-slate-500">
            Approved change orders adjust the contract value and substantial-completion date automatically.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew}
          data-testid="new-change-order-btn">
          <Plus className="w-4 h-4 mr-2" />New Change Order
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="co-summary">
          <Stat label="Original contract" value={fmt$(summary.original_contract_value)}
            sub={project?.estimate_number || ""} />
          <Stat label="Approved changes" value={fmt$(summary.approved_change_total)}
            accent={summary.approved_change_total ? "text-emerald-600" : "text-slate-900"}
            sub={`${summary.counts.approved} approved · ${summary.approved_schedule_days} days`} />
          <Stat label="Pending changes" value={fmt$(summary.pending_change_total)}
            accent={summary.pending_change_total ? "text-amber-600" : "text-slate-900"}
            sub={`${summary.counts.submitted} awaiting approval`} />
          <Stat label="Revised contract value" value={fmt$(summary.revised_contract_value)}
            accent="text-blue-700" sub="Flows into project budget" />
          <Stat label="Revised completion"
            value={summary.revised_end_date || summary.original_end_date || "TBD"}
            sub={summary.approved_schedule_days ? `+${summary.approved_schedule_days} calendar days` : "No schedule impact"} />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white" data-testid="co-empty">
          <FileWarning className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No change orders on this project yet</p>
          <p className="text-sm text-slate-400 mt-1">Extras and unforeseen conditions get priced here, then signed by the owner.</p>
          <Button size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" onClick={openNew}>
            Create the first one
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[920px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["CO", "Title / reason", "Performed by", "Days", "Amount", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((co) => (
                <tr key={co.id} className="hover:bg-slate-50/70" data-testid={`co-row-${co.number}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-blue-700">{co.number}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{co.title}</p>
                    <p className="text-xs text-slate-500">
                      {CO_TYPES.find((t) => t.value === co.co_type)?.label || co.co_type}
                      {co.origin === "contractor_request" ? " · contractor request" : ""}
                      {co.division_code ? ` · ${co.division_code}` : ""}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{co.responsible_company || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {co.schedule_impact_days ? `${co.schedule_impact_days > 0 ? "+" : ""}${co.schedule_impact_days}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-slate-900 whitespace-nowrap">{fmt$(co.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[co.status]}`}>
                      {co.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {["draft", "rejected"].includes(co.status) && (
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                          onClick={() => act(co, "submit")} data-testid={`co-submit-${co.number}`}>
                          <Send className="w-3.5 h-3.5 mr-1" />Submit
                        </Button>
                      )}
                      {["draft", "submitted"].includes(co.status) && (
                        <>
                          <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                            onClick={() => { setApproving(co); setOwnerSigner(""); }}
                            data-testid={`co-approve-${co.number}`}>
                            <Check className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                            onClick={() => act(co, "reject", { note: window.prompt("Reason for rejection?") || "" })}
                            data-testid={`co-reject-${co.number}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {co.status === "approved" && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:bg-slate-100"
                          onClick={() => act(co, "void", { note: window.prompt("Why is this being voided?") || "" })}
                          data-testid={`co-void-${co.number}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" />Void
                        </Button>
                      )}
                      {["rejected", "void"].includes(co.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                          onClick={() => act(co, "reopen")}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!["approved", "void"].includes(co.status) && (
                        <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                          onClick={() => openEdit(co)} data-testid={`co-edit-${co.number}`}>Edit</Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                        onClick={() => openPdf(co)} data-testid={`co-pdf-${co.number}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {co.status !== "approved" && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          onClick={() => remove(co)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit change order" : "New change order"}</DialogTitle>
            <DialogDescription>
              Price the extra work. Nothing moves the contract value until it is approved.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Title</label>
                  <Input value={form.title}
                    placeholder="Added negative-pressure anteroom at Room 4"
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="co-title-input" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Reason</label>
                  <Select value={form.co_type} onValueChange={(v) => setForm({ ...form, co_type: v })}>
                    <SelectTrigger data-testid="co-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CO_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Division / scope code</label>
                  <Input value={form.division_code} placeholder="DIV 23 · HVAC"
                    onChange={(e) => setForm({ ...form, division_code: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Performed by</label>
                  <Input value={form.responsible_company} placeholder="Trade partner"
                    onChange={(e) => setForm({ ...form, responsible_company: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Markup %</label>
                    <Input type="number" value={form.markup_pct}
                      onChange={(e) => setForm({ ...form, markup_pct: e.target.value })}
                      data-testid="co-markup-input" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Schedule days</label>
                    <Input type="number" value={form.schedule_impact_days}
                      onChange={(e) => setForm({ ...form, schedule_impact_days: e.target.value })}
                      data-testid="co-days-input" />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Description of change</label>
                <Textarea rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="co-description-input" />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Justification</label>
                <Textarea rows={2} value={form.justification}
                  placeholder="Requested by Infection Prevention after ICRA Class IV review."
                  onChange={(e) => setForm({ ...form, justification: e.target.value })} />
              </div>

              <div className="rounded-xl border border-slate-200">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 rounded-t-xl">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-600">Pricing</p>
                  <Button size="sm" variant="outline"
                    onClick={() => setForm({ ...form, line_items: [...form.line_items, emptyItem()] })}
                    data-testid="co-add-line-btn">
                    <Plus className="w-3 h-3 mr-1" />Add line
                  </Button>
                </div>
                <div className="p-3 space-y-2">
                  <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    <span className="col-span-5">Description</span><span>Qty</span><span>UOM</span>
                    <span className="col-span-2">Price to owner</span><span className="col-span-2">Our cost</span>
                    <span className="text-right">Ext.</span>
                  </div>
                  {form.line_items.map((li, i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                      <Input className="sm:col-span-5" placeholder="Description"
                        value={li.description || ""} onChange={(e) => setItem(i, "description", e.target.value)}
                        data-testid={`co-line-desc-${i}`} />
                      <Input type="number" value={li.qty ?? ""} onChange={(e) => setItem(i, "qty", e.target.value)} />
                      <Input value={li.uom || ""} onChange={(e) => setItem(i, "uom", e.target.value)} />
                      <Input type="number" className="sm:col-span-2"
                        value={li.unit_price ?? ""} onChange={(e) => setItem(i, "unit_price", e.target.value)}
                        data-testid={`co-line-price-${i}`} />
                      <Input type="number" className="sm:col-span-2"
                        value={li.unit_cost ?? ""} onChange={(e) => setItem(i, "unit_cost", e.target.value)} />
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs font-bold text-slate-700">
                          {fmt$(Number(li.qty || 0) * Number(li.unit_price || 0))}
                        </span>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, j) => j !== i) })}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap justify-end gap-6 pt-2 border-t border-slate-200 text-sm">
                    <span className="text-slate-500">Subtotal <b className="text-slate-800">{fmt$(subtotal)}</b></span>
                    <span className="text-slate-500">Markup <b className="text-slate-800">{fmt$(markup)}</b></span>
                    <span className="text-slate-500">Our cost <b className="text-slate-800">{fmt$(costTotal)}</b></span>
                    <span className="text-slate-500">Margin <b className="text-emerald-600">{fmt$(subtotal + markup - costTotal)}</b></span>
                    <span className="text-slate-900 font-black" data-testid="co-form-total">
                      Total {fmt$(subtotal + markup)}
                    </span>
                  </div>
                  {summary && (
                    <p className="text-xs text-slate-500 text-right">
                      Contract value if approved: <b className="text-blue-700">
                        {fmt$(Number(summary.revised_contract_value || 0) + (editingId ? 0 : subtotal + markup))}
                      </b>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="co-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? "Save changes" : "Create change order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve */}
      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve {approving?.number}</DialogTitle>
            <DialogDescription>Record the owner signature and update the contract.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This adds <b className="text-emerald-600">{fmt$(approving?.total)}</b> to the contract value
              {approving?.schedule_impact_days
                ? <> and {approving.schedule_impact_days > 0 ? "extends" : "pulls in"} substantial completion by <b>{Math.abs(approving.schedule_impact_days)} days</b></>
                : null}.
            </p>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Contract value now</span><b className="text-slate-800">{fmt$(summary?.revised_contract_value)}</b>
              </div>
              <div className="flex justify-between text-slate-500 mt-1">
                <span>After approval</span>
                <b className="text-blue-700 inline-flex items-center gap-1">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  {fmt$(Number(summary?.revised_contract_value || 0) + Number(approving?.total || 0))}
                </b>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Owner representative who signed</label>
              <Input value={ownerSigner} placeholder="M. Reyes, Harris Health Facilities"
                onChange={(e) => setOwnerSigner(e.target.value)} data-testid="co-owner-signer-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproving(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="co-approve-confirm-btn"
              onClick={() => { act(approving, "approve", { owner_signed_by: ownerSigner }); setApproving(null); }}>
              <Check className="w-4 h-4 mr-2" />Approve &amp; update contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
